import React, { useState } from 'react';
import { CleaveInput, preventSubmit, Spinner } from '@cp-shared-19/frontend-ui';
import { Button, Fieldset, Form, Layout } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { RegistrationErrorNotification } from '../../registration-error';
import {
    RegistrationContractSubmissionState,
    useContractRegistrationSubmission,
} from './useContractRegistrationSubmission';
import { validationSchema } from './validation';
import { CustomerInformationWithContract } from '../RegistrationFormDataTypes';

export const testIds: Record<string, string> = {
    contractNumber: 'contract-number',
    submitButton: 'submit-registration-button',
};

interface RegistrationProps {
    onSuccess: (customerInfo?: CustomerInformationWithContract) => void;
}

export const initialValues: RegistrationContractSubmissionState = {
    contractNumber: '',
};

const translationPrefix = 'registration-form.contract.input-labels';

const RegistrationContract: React.FC<RegistrationProps> = ({ onSuccess }) => {
    const { t } = useTranslation(['registration', 'loader']);
    const { submit, errorCode, isLoading, data } = useContractRegistrationSubmission(onSuccess);
    const [submited, setSubmited] = useState(false);
    const onReset = () => {
        setSubmited(false);
        onSuccess(undefined);
    };
    return (
        <>
            {isLoading && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <Layout.Item>
                <RegistrationErrorNotification errorCode={errorCode} />
            </Layout.Item>
            <Layout.Item default={'1/1'}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema(t)}
                    onSubmit={(d) => {
                        submit(d);
                        setSubmited(true);
                    }}
                >
                    {(formik) => (
                        <Form onSubmit={(e) => preventSubmit(e)}>
                            <Fieldset>
                                <Fieldset.Row>
                                    <CleaveInput
                                        cleaveOptions={{
                                            numericOnly: false,
                                        }}
                                        className="c-form-field--no-floating"
                                        label={`${t(`${translationPrefix}.contract-number`)} `}
                                        name={'contractNumber'}
                                        testId={testIds.contractNumber}
                                        disabled={!!data && submited}
                                        withHtmlValidationWarning
                                        tooltip={`${t(`${translationPrefix}.contract-number-tooltip`)} `}
                                        tooltipTitle={`${t(`${translationPrefix}.contract-number-tooltip-title`)} `}
                                        aria-required={true}
                                        isMandatory={true}
                                        requiredText=" *"
                                    />
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    {(!data || !submited) && (
                                        <Button
                                            full={true}
                                            type="submit"
                                            disabled={!!data && submited}
                                            onClick={() => formik.submitForm()}
                                            testId={testIds.submitButton}
                                        >
                                            {t(`registration-form.button-labels.confirm`)}
                                        </Button>
                                    )}
                                    {data && submited && (
                                        <Button
                                            secondary
                                            full={true}
                                            type="submit"
                                            onClick={() => {
                                                formik.resetForm();
                                                onReset();
                                            }}
                                            testId={testIds.submitButton}
                                        >
                                            {t(`registration-form.button-labels.back`)}
                                        </Button>
                                    )}
                                </Fieldset.Row>
                            </Fieldset>
                        </Form>
                    )}
                </Formik>
            </Layout.Item>
        </>
    );
};

export default RegistrationContract;
